// Reports to Loki, from Artemis Web, whether Artemis Helper in this same
// browser is alive. A Helper that stops (killed, switched off) cannot log its
// own death; this page, left open all day, logs it within seconds, next to the
// Helper's own lines (same user and device_id).

import { useEffect } from "react";
import {
	type HelperPresenceStatus,
	onHelperGone,
	probeHelper,
} from "../api/helper-presence";
import { LokiLogger } from "../api/lokiLogger";
import { useAuth } from "../context/AuthContext";

/** How often the page asks the Helper; only a changed answer is logged. */
const PROBE_INTERVAL_MS = 60 * 1000;
/** An unchanged answer is logged at most this often; a change at once. */
const UNCHANGED_LOG_INTERVAL_MS = 30 * 60 * 1000;

/** Probes the Helper on load and every few minutes while signed in. Not while
 * impersonating: the Helper answering is the admin's own, not the user's. */
export function useHelperPresenceReporting(): void {
	const { isAuthenticated, isImpersonating } = useAuth();

	useEffect(() => {
		if (!isAuthenticated || isImpersonating) return;

		let lastStatus: HelperPresenceStatus | null = null;
		let lastLoggedAt = 0;
		let cancelled = false;

		const probeAndReport = async (): Promise<void> => {
			const presence = await probeHelper();
			if (cancelled) return;
			const now = Date.now();
			if (
				presence.status === lastStatus &&
				now - lastLoggedAt < UNCHANGED_LOG_INTERVAL_MS
			) {
				return;
			}
			const changed = lastStatus !== null && presence.status !== lastStatus;
			lastStatus = presence.status;
			lastLoggedAt = now;
			const labels: Record<string, string> = {
				module: "artemis_web",
				file: "frontend/hooks/use-helper-presence-reporting.ts",
				helper_status: presence.status,
				page_visible: String(document.visibilityState === "visible"),
			};
			for (const [key, value] of Object.entries(presence)) {
				if (key !== "status" && value !== undefined && value !== null) {
					labels[`helper_${key}`] = String(value);
				}
			}
			LokiLogger.log(
				presence.status === "alive" ? "info" : "warning",
				`Artemis Web: Artemis Helper ${presence.status}${changed ? " (changed)" : ""}`,
				labels,
				localStorage.getItem("access_token") ?? undefined
			);
		};

		// The Helper's death, the moment a content script in this page sees it,
		// instead of at the next probe.
		const stopWatching = onHelperGone((gone) => {
			lastStatus = "context_invalidated";
			lastLoggedAt = Date.now();
			LokiLogger.log(
				"warning",
				`Artemis Web: Artemis Helper gone at ${gone.detected_at} (alive since ${gone.watching_since})`,
				{
					module: "artemis_web",
					file: "frontend/hooks/use-helper-presence-reporting.ts",
					helper_status: "gone",
					helper_gone_at: gone.detected_at,
					helper_alive_since: gone.watching_since,
					page_visible: String(document.visibilityState === "visible"),
				},
				localStorage.getItem("access_token") ?? undefined
			);
		});

		probeAndReport();
		const id = window.setInterval(probeAndReport, PROBE_INTERVAL_MS);
		return () => {
			cancelled = true;
			window.clearInterval(id);
			stopWatching();
		};
	}, [isAuthenticated, isImpersonating]);
}
